var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { observable, runInAction } from 'mobx';
import { Observer } from 'mobx-react-lite';
import React from 'react';
import { ContextColor } from '../../constants/color.enum';
import { SupportGroupTopicEndpoints } from '../../endpoints/supportGroupTopic.endpoints';
import { useControllers } from '../../hooks/controller.hooks';
import { useOnMount } from '../../hooks/lifecycle.hooks';
import { getSupportGroups } from '../../requests/getSupportGroups.request';
import { transformArray } from '../../utils/array.utils';
import joinClassName from '../../utils/className.utils';
import { getContextColorStyle } from '../../utils/color.utils';
import { useProps, useStore } from '../../utils/mobx.utils';
import { SupportGroupUserGroupTypeDefs } from '../../utils/supportGroup.helpers';
import { BaseCalendarState } from '../BaseCalendar/BaseCalendarState';
import BaseLabel from '../BaseLabel/BaseLabel';
import BaseSelector from '../BaseSelector/BaseSelector';
import { CommandList } from '../CommandList/CommandList';
import { CommandListItem } from '../CommandList/CommandListItem';
import IndexDirectory from '../IndexDirectory/IndexDirectory';
import IndexDirectoryState from '../IndexDirectory/IndexDirectoryState';
import LoadingIndicator from '../LoadingIndicator/LoadingIndicator';
import SupportGroupCalendarEventRenderer from '../SupportGroupCalendarEventRenderer/SupportGroupCalendarEventRenderer';
import './SupportGroupsIndex.scss';
var DEFAULT_AGE_GROUP_FILTER_OPTIONS = SupportGroupUserGroupTypeDefs.map(function (def) {
    var _a;
    return ({
        label: def.value === '18+' ? 'Adults' : def.value,
        value: def.value,
        color: (_a = def.clientColor) !== null && _a !== void 0 ? _a : def.color,
    });
});
var getTopicUniqueId = function (topic) {
    return topic.id + "_" + topic.slug;
};
var SupportGroupsIndex = function (props) {
    var _a = useControllers(), API = _a.API, UI = _a.UI;
    var p = useProps(props);
    var s = useStore(function () {
        var _a;
        return ({
            supportGroups: [],
            indexDirectoryState: new IndexDirectoryState({
                viewMode: (_a = p.defaultMode) !== null && _a !== void 0 ? _a : 'calendar',
                sortByKeyName: 'timeScheduled',
                sortDirection: 'desc',
                searchable: true,
                searchByKeyName: 'title',
            }),
            calendarState: new BaseCalendarState({
                name: 'support-groups-index',
                mode: p.defaultCalendarMode || 'week',
                fullHeight: true,
                fitInnerToAvailableHeight: true,
                eventTypeGroups: [],
            }),
            calendarEvents: [],
            get canCreate() { return p.canCreate; },
            get fullHeight() { return s.calendarState.fullHeight; },
            get titleFilter() {
                return function (g) { return true; };
            },
            get archiveFilter() {
                return function (g) { return false || !g.timeArchived; };
            },
            get ageGroupFilter() {
                return function (g) {
                    if (!s.filterByAgeGroups || p.canEdit)
                        return true;
                    if (!g.tags)
                        return true;
                    return g.ageGroups.filter(function (ag) { return s.filterByAgeGroups.includes(ag); }).length > 0;
                };
            },
            get topicFilter() {
                return function (g) {
                    if (!s.filterByTopic)
                        return true;
                    return g.topic && getTopicUniqueId(g.topic) === s.filterByTopic;
                };
            },
            get compositeFilter() {
                return function (g) { return s.archiveFilter(g) && s.ageGroupFilter(g) && s.titleFilter(g) && s.topicFilter(g); };
            },
            get shouldShowCommandList() {
                return p.showFilters;
            },
            topics: [],
            topicsLoaded: false,
            topicsLoadError: false,
            filterByTopic: '',
            get filterByTopicOptions() {
                return s.topics.map(function (t) { return ({
                    label: t.displayName,
                    color: t.color,
                    value: getTopicUniqueId(t),
                }); });
            },
            filterByAgeGroups: DEFAULT_AGE_GROUP_FILTER_OPTIONS.map(function (op) { return op.value; }),
            _tagsFromParams: [],
            get filterableTags() {
                if (p.canEdit)
                    return [];
                return s._tagsFromParams;
            },
            get filterByAgeGroupsOptions() {
                return DEFAULT_AGE_GROUP_FILTER_OPTIONS;
            },
        });
    });
    useOnMount(function () {
        var getSupportGroupTopics = function () { return __awaiter(void 0, void 0, void 0, function () {
            var url, response, e_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        url = SupportGroupTopicEndpoints.public.index();
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, API.getMany(url)];
                    case 2:
                        response = _a.sent();
                        return [3 /*break*/, 4];
                    case 3:
                        e_1 = _a.sent();
                        s.topicsLoadError = true;
                        return [3 /*break*/, 4];
                    case 4:
                        runInAction(function () {
                            // @ts-ignore
                            s.topics = response === null || response === void 0 ? void 0 : response.data.data;
                            s.topicsLoaded = true;
                        });
                        return [2 /*return*/];
                }
            });
        }); };
        getSupportGroupTopics();
    });
    var style = __assign({}, getContextColorStyle(ContextColor.Primary, 'deepPurple'));
    var eventBuilder = function (g) {
        return observable({
            get id() { return g.id; },
            get startTime() { return g.timeScheduled || g.timeStarted; },
            get name() { return g.title; },
            get hidden() { return !s.compositeFilter(g); },
            get color() { var _a, _b; return (_b = (_a = g.topic) === null || _a === void 0 ? void 0 : _a.color) !== null && _b !== void 0 ? _b : g.color; },
            data: g,
        });
    };
    var calendarDataFetcher = function (startDate, endDate) { return new Promise(function (resolve, reject) { return __awaiter(void 0, void 0, void 0, function () {
        var params, supportGroups, events, e_2;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    _a.trys.push([0, 2, , 3]);
                    params = {
                        // @ts-ignore
                        filter: {
                            dateRange: {
                                key: 'timeScheduled',
                                startDate: startDate,
                                endDate: endDate,
                            }
                        },
                        perPage: Infinity,
                        latest: true,
                        include: ['topic', 'facilitator'],
                    };
                    return [4 /*yield*/, getSupportGroups(API, params)];
                case 1:
                    supportGroups = _a.sent();
                    events = transformArray(supportGroups, eventBuilder);
                    resolve(events);
                    return [3 /*break*/, 3];
                case 2:
                    e_2 = _a.sent();
                    reject(e_2);
                    return [3 /*break*/, 3];
                case 3: return [2 /*return*/];
            }
        });
    }); }); };
    return React.createElement(Observer, { children: function () { return (React.createElement("div", { className: joinClassName('SupportGroupsIndex', s.fullHeight && 'fullHeight', p.disabled && 'disabled'), style: style },
            s.shouldShowCommandList && React.createElement(CommandList, { direction: "row" },
                React.createElement(CommandListItem, null,
                    React.createElement(BaseLabel, null, "FILTER"),
                    React.createElement(BaseSelector, { appearance: "inline", form: s, field: "filterByAgeGroups", options: s.filterByAgeGroupsOptions, nullable: true })),
                !s.topicsLoadError && React.createElement(CommandListItem, null,
                    React.createElement(BaseLabel, null, "TOPIC"),
                    s.topicsLoaded
                        ? React.createElement(BaseSelector, { appearance: "system", form: s, field: "filterByTopic", options: s.filterByTopicOptions, placeholder: "All Topics" })
                        : React.createElement(LoadingIndicator, null))),
            React.createElement(IndexDirectory, { state: s.indexDirectoryState, allowedViewModes: p.allowedViewModes || ['calendar'], 
                // dataFetcher={tableDataFetcher}
                appearanceOptions: p.appearanceOptions, doNotShowCreateButtonInOptions: Boolean(p.addButtonRenderPortalRef), calendarPropsGetter: function () { return ({
                    state: s.calendarState,
                    events: s.calendarEvents,
                    eventBuilder: eventBuilder,
                    showHeader: true,
                    dataFetcher: calendarDataFetcher,
                    eventRenderer: SupportGroupCalendarEventRenderer,
                }); } }))); } });
};
export default SupportGroupsIndex;
